import type { Price, VariantInfo } from 'src/types';
import type { IVariantValue, VariantType } from '../types';
import { FilterType } from 'src/components/price-list/types';

const getVariantProp = (variantType: VariantType, variant: IVariantValue) => {
  switch (variantType) {
    case FilterType.VARIANT_SIZE:
      return variant.size;

    case FilterType.VARIANT_COLOR:
      return variant.color;

    default:
      return '';
  }
};

const getDistinct = (
  variantType: VariantType,
  values: IVariantValue[]
): IVariantValue[] => {
  if (variantType === FilterType.VARIANT_SIZE) {
    return [
      ...new Map(
        values.filter(v => v.size).map(item => [item.size, item])
      ).values(),
    ];
  }
  if (variantType === FilterType.VARIANT_COLOR) {
    return [
      ...new Map(
        values.filter(v => v.color).map(item => [item.color, item])
      ).values(),
    ];
  }
  return [];
};

const getValuesFromVariant = (
  variantType: VariantType,
  variant?: VariantInfo | null
): IVariantValue[] => {
  const values: IVariantValue[] = [];
  if (
    variantType === FilterType.VARIANT_SIZE &&
    variant?.size &&
    variant?.sizeSystem
  ) {
    const val = {
      size: variant?.size,
      sizeSystem: variant?.sizeSystem,
    };
    values.push(val);
  }
  if (
    variantType === FilterType.VARIANT_COLOR &&
    variant?.colors &&
    variant?.colors?.length > 0
  ) {
    variant?.colors.forEach(color => {
      values.push({ color });
    });
  }
  return values;
};

const disableVariantValues = (
  variantType: VariantType,
  allVariantValues: IVariantValue[],
  currentVariantValues: IVariantValue[]
): IVariantValue[] => {
  return allVariantValues.map(variant => {
    return {
      ...variant,
      disabled: !currentVariantValues.some(
        current =>
          getVariantProp(variantType, variant) ===
          getVariantProp(variantType, current)
      ),
    };
  });
};

export const getDistinctVariantValues = (
  variantType: VariantType,
  offers: Price[]
): IVariantValue[] => {
  const allValues: IVariantValue[] = [];
  offers.forEach(offer => {
    const values = getValuesFromVariant(variantType, offer.variantInfo);
    if (values.length > 0) {
      allValues.push(...values);
    }
    offer.alternativePrices?.forEach(alt => {
      const altValues = getValuesFromVariant(variantType, alt?.variantInfo);
      if (altValues.length > 0) {
        allValues.push(...altValues);
      }
    });
  });
  return getDistinct(variantType, allValues);
};

export const getVariantValues = (
  variantType: VariantType,
  filteredOffers: Price[],
  allOffers: Price[]
): IVariantValue[] => {
  const filteredVariantsValues = getDistinctVariantValues(
    variantType,
    filteredOffers
  );
  const allVariantValues = getDistinctVariantValues(variantType, allOffers);
  return disableVariantValues(
    variantType,
    allVariantValues,
    filteredVariantsValues
  );
};
